import { useState } from 'preact/hooks';
import { api } from '../api.js';
import { household, goBack, showToast, friendlyError } from '../store.js';
import { __ } from '../i18n.js';
import { SUBSCRIPTION_CATEGORIES, BILLING_PERIODS } from '../lib/subscriptions.js';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

/** Integer cents -> a plain euro string for the input, e.g. 1999 -> "19.99". */
function centsToEuroInput(cents) {
  return cents || cents === 0 ? (cents / 100).toFixed(2) : '';
}

/** "19,99" or "19.99" (either decimal separator, Dutch keyboards use a comma) -> cents. */
function euroInputToCents(value) {
  const normalized = String(value).replace(',', '.').trim();
  const euros = parseFloat(normalized);
  return Number.isFinite(euros) ? Math.round(euros * 100) : 0;
}

/**
 * HH-2: one form, no wizard (plan §4.1) — every field flows top to bottom
 * with no collapsible "Meer opties" section, unlike TaskFormScreen. The
 * module is genuinely this simple: CRUD + date fields + one money field.
 */
export default function SubscriptionFormScreen({ params }) {
  const subscription = params && params.subscription;
  const isEdit = Boolean(subscription && subscription.id);
  const isArchived = Boolean(subscription && subscription.archived_at);

  const [name, setName] = useState(subscription?.name || '');
  const [provider, setProvider] = useState(subscription?.provider || '');
  const [category, setCategory] = useState(subscription?.category || SUBSCRIPTION_CATEGORIES[0].value);
  const [costEuros, setCostEuros] = useState(centsToEuroInput(subscription?.cost_cents));
  const [billingPeriod, setBillingPeriod] = useState(subscription?.billing_period || 'monthly');
  const [contractStart, setContractStart] = useState(subscription?.contract_start || '');
  const [contractEnd, setContractEnd] = useState(subscription?.contract_end || '');
  const [noticePeriodDays, setNoticePeriodDays] = useState(
    subscription?.notice_period_days ?? ''
  );
  const [autoRenews, setAutoRenews] = useState(subscription ? !!subscription.auto_renews : true);
  const [warnDaysBefore, setWarnDaysBefore] = useState(subscription?.warn_days_before ?? '');
  const [notes, setNotes] = useState(subscription?.notes || '');
  // "scope choice with household preselected" (plan §4.1 spec) — only
  // offered on create, same reasoning as CategoryScreen's own is_shared
  // checkbox: the backend never lets scope change after creation.
  const [isShared, setIsShared] = useState(!!household.value);
  const [saving, setSaving] = useState(false);

  function buildPayload() {
    return {
      name: name.trim(),
      provider: provider.trim(),
      category,
      cost_cents: euroInputToCents(costEuros),
      billing_period: billingPeriod,
      contract_start: contractStart,
      contract_end: contractEnd,
      notice_period_days: noticePeriodDays === '' ? '' : Number(noticePeriodDays),
      auto_renews: autoRenews,
      warn_days_before: warnDaysBefore === '' ? '' : Number(warnDaysBefore),
      notes,
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      showToast(__('Geef het abonnement een naam.', 'etm'), 'error');
      return;
    }

    setSaving(true);
    try {
      if (isEdit) {
        await api.subscriptions.update(subscription.id, buildPayload());
        showToast(__('Abonnement opgeslagen.', 'etm'), 'success');
      } else {
        const payload = buildPayload();
        payload.is_shared = isShared;
        await api.subscriptions.create(payload);
        showToast(__('Abonnement toegevoegd.', 'etm'), 'success');
      }
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  // Archive is the primary retire action (plan §4.1: "archive instead of
  // delete") — reversible, and the row keeps its history. Hard delete is
  // only offered once a row is already archived, so a slip of the finger
  // on a live subscription can never permanently lose it in one tap.
  async function handleArchiveToggle() {
    setSaving(true);
    try {
      await api.subscriptions.update(subscription.id, { archived: !isArchived });
      showToast(isArchived ? __('Abonnement hersteld.', 'etm') : __('Abonnement gearchiveerd.', 'etm'), 'success');
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm(__('Abonnement permanent verwijderen?', 'etm'))) return;
    try {
      await api.subscriptions.remove(subscription.id);
      showToast(__('Abonnement verwijderd.', 'etm'), 'success');
      goBack();
    } catch (err) {
      showToast(friendlyError(err), 'error');
    }
  }

  return (
    <div>
      <BackButton onClick={goBack} />
      <h1 class="etm-screen-title">
        {isEdit ? __('Abonnement bewerken', 'etm') : __('Nieuw abonnement', 'etm')}
      </h1>

      <form onSubmit={handleSubmit}>
        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-sub-name">
            {__('Naam', 'etm')}
          </label>
          <input
            id="etm-sub-name"
            class="etm-form-input"
            type="text"
            value={name}
            onInput={(e) => setName(e.target.value)}
            placeholder={__('Bijv. Ziggo internet', 'etm')}
            autofocus
            required
          />
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-sub-provider">
            {__('Aanbieder', 'etm')}
          </label>
          <input
            id="etm-sub-provider"
            class="etm-form-input"
            type="text"
            value={provider}
            onInput={(e) => setProvider(e.target.value)}
            placeholder={__('Optioneel', 'etm')}
          />
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-sub-category">
            {__('Categorie', 'etm')}
          </label>
          <select
            id="etm-sub-category"
            class="etm-form-select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {SUBSCRIPTION_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label()}
              </option>
            ))}
          </select>
        </div>

        <div class="etm-form-row">
          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-sub-cost">
              {__('Kosten (€)', 'etm')}
            </label>
            <input
              id="etm-sub-cost"
              class="etm-form-input"
              type="text"
              inputMode="decimal"
              value={costEuros}
              onInput={(e) => setCostEuros(e.target.value)}
              placeholder="0,00"
            />
          </div>
          <div class="etm-form-group">
            <label class="etm-form-label">{__('Facturatie', 'etm')}</label>
            <div class="etm-toggle-row">
              {BILLING_PERIODS.map((period) => (
                <button
                  key={period.value}
                  type="button"
                  class={billingPeriod === period.value ? 'active' : ''}
                  onClick={() => setBillingPeriod(period.value)}
                >
                  {period.label()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div class="etm-form-row">
          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-sub-start">
              {__('Contract start', 'etm')}
            </label>
            <input
              id="etm-sub-start"
              class="etm-form-input"
              type="date"
              value={contractStart}
              onInput={(e) => setContractStart(e.target.value)}
            />
          </div>
          <div class="etm-form-group">
            <label class="etm-form-label" for="etm-sub-end">
              {__('Contract einde', 'etm')}
            </label>
            <input
              id="etm-sub-end"
              class="etm-form-input"
              type="date"
              value={contractEnd}
              onInput={(e) => setContractEnd(e.target.value)}
            />
            <p class="etm-field-hint">{__('Leeg laten als er geen einddatum is (bijv. streaming).', 'etm')}</p>
          </div>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-sub-notice">
            {__('Opzegtermijn (dagen)', 'etm')}
          </label>
          <input
            id="etm-sub-notice"
            class="etm-form-input"
            type="number"
            min="0"
            value={noticePeriodDays}
            onInput={(e) => setNoticePeriodDays(e.target.value)}
            placeholder={__('Optioneel', 'etm')}
          />
        </div>

        <div class="etm-form-group etm-checkbox-group">
          <input
            id="etm-sub-auto-renews"
            type="checkbox"
            checked={autoRenews}
            onChange={(e) => setAutoRenews(e.currentTarget.checked)}
          />
          <label for="etm-sub-auto-renews">{__('Verlengt automatisch', 'etm')}</label>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-sub-warn">
            {__('Herinner me dagen van tevoren', 'etm')}
          </label>
          <input
            id="etm-sub-warn"
            class="etm-form-input"
            type="number"
            min="0"
            value={warnDaysBefore}
            onInput={(e) => setWarnDaysBefore(e.target.value)}
            placeholder="30"
          />
          <p class="etm-field-hint">{__('Wordt gebruikt zodra de herinneringsmail bestaat.', 'etm')}</p>
        </div>

        <div class="etm-form-group">
          <label class="etm-form-label" for="etm-sub-notes">
            {__('Notities', 'etm')}
          </label>
          <textarea
            id="etm-sub-notes"
            class="etm-form-textarea"
            value={notes}
            onInput={(e) => setNotes(e.target.value)}
            placeholder={__('Optioneel', 'etm')}
          />
        </div>

        {!isEdit && household.value ? (
          <div class="etm-form-group etm-checkbox-group">
            <input
              id="etm-sub-shared"
              type="checkbox"
              checked={isShared}
              onChange={(e) => setIsShared(e.currentTarget.checked)}
            />
            <label for="etm-sub-shared">{__('Gedeeld met huishouden', 'etm')}</label>
          </div>
        ) : null}

        <button type="submit" class="etm-btn etm-btn-primary" disabled={saving}>
          {isEdit ? __('Opslaan', 'etm') : __('Abonnement toevoegen', 'etm')}
        </button>
      </form>

      {isEdit ? (
        <button
          type="button"
          class="etm-btn etm-btn-secondary"
          disabled={saving}
          onClick={handleArchiveToggle}
        >
          <Icon name={isArchived ? 'unarchive' : 'archive'} size={20} />
          {isArchived ? __('Herstellen', 'etm') : __('Archiveren', 'etm')}
        </button>
      ) : null}

      {isEdit && isArchived ? (
        <button type="button" class="etm-btn etm-btn-danger" onClick={handleDelete}>
          {__('Verwijderen', 'etm')}
        </button>
      ) : null}
    </div>
  );
}
